import React, { useState, useEffect } from 'react';
import { api } from '../context/AppContext';
import AdminNavbar from '../components/AdminNavbar';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testingTemplate, setTestingTemplate] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchTemplates();
    fetchSettings();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/email-templates');
      setTemplates(res.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await api.get('/email-templates/settings');
      setSettings(res.data);
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  const handleEdit = (template) => {
    setEditingId(template._id);
    setFormData({
      subject: template.subject,
      enabled: template.enabled,
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({});
  };

  const handleSave = async (templateId) => {
    try {
      await api.put(`/email-templates/${templateId}`, formData);
      toast.success('Template updated successfully!');
      setEditingId(null);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const handleTestEmail = async (templateKey) => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setTestingTemplate(templateKey);
    try {
      await api.post('/email-templates/test', {
        templateKey,
        testEmail,
      });
      toast.success(`Test email sent to ${testEmail}!`);
      setTestEmail('');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send test email');
    } finally {
      setTestingTemplate(null);
    }
  };

  const templateIcons = {
    order_confirmation: '📦',
    order_status: '📊',
    welcome: '👋',
    contact_form: '📬',
    order_delivered: '🎉',
  };

  if (loading) {
    return (
      <div>
        <AdminNavbar />
        <div className="flex justify-center items-center min-h-screen">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavbar />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2">
            📧 Email Templates
          </h1>
          <p className="text-gray-600">
            Manage all email templates in one place. Customize subjects and enable/disable templates.
          </p>
        </div>

        {/* SMTP Settings Info */}
        {settings && (
          <div className="mb-6 p-5 bg-blue-50 border border-blue-200 rounded-xl">
            <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
              <span>⚙️</span>
              <span>Email Configuration</span>
            </h3>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-blue-700 font-semibold mb-1">SMTP Host</p>
                <p className="text-blue-900">{settings.host}</p>
              </div>
              <div>
                <p className="text-blue-700 font-semibold mb-1">Port</p>
                <p className="text-blue-900">{settings.port}</p>
              </div>
              <div>
                <p className="text-blue-700 font-semibold mb-1">From Email</p>
                <p className="text-blue-900">{settings.user}</p>
              </div>
              <div>
                <p className="text-blue-700 font-semibold mb-1">From Name</p>
                <p className="text-blue-900">{settings.fromName}</p>
              </div>
            </div>
          </div>
        )}

        {/* Test Email Section */}
        <div className="mb-6 p-5 bg-amber-50 border border-amber-200 rounded-xl">
          <h3 className="font-bold text-amber-900 mb-3 flex items-center gap-2">
            <span>🧪</span>
            <span>Test Email Delivery</span>
          </h3>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-amber-900 mb-2">
                Enter test email address
              </label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                className="w-full px-4 py-2 border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>
          <p className="text-xs text-amber-700 mt-2">
            💡 Use the "Send Test" button on each template to test email delivery
          </p>
        </div>

        {/* Templates List */}
        <div className="space-y-4">
          {templates.map((template) => {
            const isEditing = editingId === template._id;
            const isTesting = testingTemplate === template.key;

            return (
              <div
                key={template._id}
                className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden hover:border-green-300 transition-all"
              >
                {/* Template Header */}
                <div className="p-5 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-3xl">
                          {templateIcons[template.key] || '📧'}
                        </span>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">
                            {template.name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {template.description}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Status Toggle */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-gray-600">
                        {isEditing ? formData.enabled : template.enabled
                          ? 'Enabled'
                          : 'Disabled'}
                      </span>
                      <label className="relative inline-block w-12 h-6">
                        <input
                          type="checkbox"
                          checked={isEditing ? formData.enabled : template.enabled}
                          onChange={(e) => {
                            if (isEditing) {
                              setFormData({ ...formData, enabled: e.target.checked });
                            } else {
                              handleEdit({ ...template, enabled: e.target.checked });
                              setFormData({ ...template, enabled: e.target.checked });
                            }
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-12 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-400 rounded-full peer peer-checked:after:translate-x-6 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500" />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Template Body */}
                <div className="p-5">
                  {/* Subject Line */}
                  <div className="mb-4">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Email Subject
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.subject}
                        onChange={(e) =>
                          setFormData({ ...formData, subject: e.target.value })
                        }
                        className="w-full px-4 py-2 border-2 border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Enter email subject"
                      />
                    ) : (
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-900 font-mono text-sm">
                          {template.subject}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Available Variables */}
                  <div className="mb-4">
                    <p className="text-sm font-bold text-gray-700 mb-2">
                      Available Variables
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {template.variables.map((variable) => (
                        <span
                          key={variable}
                          className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-mono font-semibold"
                        >
                          {'{{' + variable + '}}'}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Use these variables in your subject line to insert dynamic content
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4 border-t border-gray-100">
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => handleSave(template._id)}
                          className="px-6 py-2 bg-green-500 text-white font-bold rounded-lg hover:bg-green-600 transition-colors"
                        >
                          ✅ Save Changes
                        </button>
                        <button
                          onClick={handleCancel}
                          className="px-6 py-2 bg-gray-200 text-gray-700 font-bold rounded-lg hover:bg-gray-300 transition-colors"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEdit(template)}
                          className="px-6 py-2 bg-blue-500 text-white font-bold rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleTestEmail(template.key)}
                          disabled={!testEmail || isTesting}
                          className="px-6 py-2 bg-amber-500 text-white font-bold rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {isTesting ? (
                            <>
                              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>🧪 Send Test</>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Help Section */}
        <div className="mt-8 p-6 bg-green-50 border border-green-200 rounded-xl">
          <h3 className="font-bold text-green-900 mb-3 flex items-center gap-2">
            <span>💡</span>
            <span>How to use Email Templates</span>
          </h3>
          <ul className="space-y-2 text-sm text-green-800">
            <li>• <strong>Enable/Disable:</strong> Toggle the switch to enable or disable any template</li>
            <li>• <strong>Edit Subject:</strong> Click "Edit" to customize the email subject line</li>
            <li>• <strong>Use Variables:</strong> Insert dynamic content using variables like {'{{'} orderId {'}}'}</li>
            <li>• <strong>Test Email:</strong> Enter a test email address and click "Send Test" to preview</li>
            <li>• <strong>Auto-send:</strong> Enabled templates will automatically send when triggered</li>
          </ul>
        </div>

        {/* Environment Variables Info */}
        <div className="mt-6 p-6 bg-gray-100 border border-gray-300 rounded-xl">
          <h3 className="font-bold text-gray-900 mb-3">🔧 Required Environment Variables</h3>
          <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-xs overflow-x-auto">
            <pre>{`# Backend .env
ZOHO_SMTP_HOST=smtp.zoho.com
ZOHO_SMTP_PORT=465
ZOHO_EMAIL_USER=your-email@yourdomain.com
ZOHO_EMAIL_PASSWORD=your-email-password
EMAIL_FROM_NAME=Protein Spot
CONTACT_PHONE=+91 98765 43210
FRONTEND_URL=https://proteinspot.in`}</pre>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
